/**
 * CÁMARA (F13.3) — getUserMedia + bucle de muestreo hacia el worker
 *
 * Trasera por defecto, cambio de cámara, linterna si el dispositivo la ofrece
 * (Android/Chrome), muestreo cada ~200 ms (nunca más de un fotograma en vuelo)
 * y cierre LIMPIO: al pausar o salir se paran todas las pistas del stream.
 * Sin red: los fotogramas sólo van del <video> a un canvas y al worker.
 */
import type { PeticionMotor, RespuestaMotor } from './motor.worker.ts';

export type ErrorCamara = 'SIN_SOPORTE' | 'DENEGADA' | 'SIN_CAMARA' | 'OCUPADA' | 'MOTOR' | 'OTRO';

export function camaraDisponible(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function' && typeof Worker !== 'undefined';
}

export function clasificarError(e: unknown): ErrorCamara {
  const n = (e as { name?: string } | null)?.name ?? '';
  if (n === 'NotAllowedError' || n === 'SecurityError' || n === 'PermissionDeniedError') return 'DENEGADA';
  if (n === 'NotFoundError' || n === 'OverconstrainedError' || n === 'DevicesNotFoundError') return 'SIN_CAMARA';
  if (n === 'NotReadableError' || n === 'TrackStartError') return 'OCUPADA';
  return 'OTRO';
}

export async function abrirCamara(idDispositivo: string | null): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: false,
    video: idDispositivo !== null
      ? { deviceId: { exact: idDispositivo }, width: { ideal: 1280 }, height: { ideal: 720 } }
      : { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
  });
}

export async function listarCamaras(): Promise<{ id: string; nombre: string }[]> {
  if (typeof navigator.mediaDevices?.enumerateDevices !== 'function') return [];
  const d = await navigator.mediaDevices.enumerateDevices();
  return d.filter((x) => x.kind === 'videoinput').map((x, i) => ({ id: x.deviceId, nombre: x.label || `Cámara ${String(i + 1)}` }));
}

export function cerrarStream(s: MediaStream | null): void {
  for (const t of s?.getTracks() ?? []) t.stop();
}

export function tieneLinterna(s: MediaStream | null): boolean {
  const t = s?.getVideoTracks()[0];
  const cap = (t?.getCapabilities?.() ?? {}) as { torch?: boolean };
  return cap.torch === true;
}

export async function linterna(s: MediaStream | null, encendida: boolean): Promise<void> {
  const t = s?.getVideoTracks()[0];
  if (t === undefined) return;
  await t.applyConstraints({ advanced: [{ torch: encendida } as MediaTrackConstraintSet] });
}

/** Motor en un worker; `crear` se puede sustituir en pruebas. */
export function crearMotor(): Worker {
  return new Worker(new URL('./motor.worker.ts', import.meta.url), { type: 'module' });
}

/**
 * Muestrea el <video> y entrega la PRIMERA lectura válida. Devuelve una función
 * para parar. Nunca hay más de un fotograma pendiente en el worker.
 */
export function muestrear(video: HTMLVideoElement, motor: Worker, alLeer: (l: { texto: string; formato: string; simbologia: string }) => void,
  alError: (e: string) => void, intervaloMs = 200): () => void {
  let parado = false;
  let enVuelo = false;
  let n = 0;
  const lienzo = document.createElement('canvas');
  const ctx = lienzo.getContext('2d', { willReadFrequently: true });
  const alMensaje = (ev: MessageEvent<RespuestaMotor>): void => {
    enVuelo = false;
    if (parado) return;
    //  Un fallo del motor (p. ej. el .wasm bloqueado por la CSP) no se arregla reintentando: se para y se avisa.
    if (ev.data.error !== undefined) { parado = true; alError(ev.data.error); return; }
    const l = ev.data.lecturas?.[0];
    if (l !== undefined) { parado = true; alLeer(l); }
  };
  const alFallo = (): void => { if (!parado) { parado = true; alError('worker'); } };
  motor.addEventListener('message', alMensaje);
  motor.addEventListener('error', alFallo);
  const tick = window.setInterval(() => {
    if (parado || enVuelo || ctx === null || video.readyState < 2 || video.videoWidth === 0) return;
    //  Se reduce a 960 px de ancho: suficiente para códigos a distancia de mano y mucho más rápido.
    const escala = Math.min(1, 960 / video.videoWidth);
    lienzo.width = Math.round(video.videoWidth * escala);
    lienzo.height = Math.round(video.videoHeight * escala);
    ctx.drawImage(video, 0, 0, lienzo.width, lienzo.height);
    enVuelo = true;
    n += 1;
    motor.postMessage({ id: n, imagen: ctx.getImageData(0, 0, lienzo.width, lienzo.height) } satisfies PeticionMotor);
  }, intervaloMs);
  return () => {
    parado = true;
    window.clearInterval(tick);
    motor.removeEventListener('message', alMensaje);
    motor.removeEventListener('error', alFallo);
  };
}
